import React, { useState, useRef, useEffect, useCallback } from 'react';
import cn from 'classnames';

import clamp from 'js/utils/clamp';
import { defineTheme, type ThemeKeys } from 'js/utils/theme-helpers';

import { ICarousel } from 'source/NFR.types';

// Are these in use? No specific styling are attached to them.
const themes = defineTheme({
  green: '-theme-green',
  small: '-theme-small'
});
type CarouselTheme = ThemeKeys<typeof themes>;

interface CarouselProps extends ICarousel {
  className?: string;
  nextButton?: (goToNext: () => void, hasNext: boolean) => React.ReactNode;
  numberOfSlidesToShow?: number;
  previousButton?: (goToPrevious: () => void, hasPrevious: boolean) => React.ReactNode;
  startIndex?: number;
  theme?: CarouselTheme | CarouselTheme[];
}

interface CarouselState {
  currentIndex: number;
  currentIndexRounded: number;
  hasNextItem: boolean;
  hasPreviousItem: boolean;
  isDragging: boolean;
  isMounted: boolean;
  shouldShowNavigation: boolean;
}

const Carousel: React.FC<React.PropsWithChildren<CarouselProps>> = ({
  children,
  className,
  nextLabel = 'Neste',
  nextButton,
  numberOfSlidesToShow = 1,
  previousButton,
  previousLabel = 'Forrige',
  startIndex = 0,
  theme
}) => {
  const itemsWrapperRef = useRef<HTMLDivElement>(null);
  const itemsTouchStartXRef = useRef<number | undefined>(0);
  const travelRef = useRef(0);
  const previousXRef = useRef(0);
  const isAnimatingRef = useRef(false);
  const isZoomedRef = useRef(false);
  const hasMultiTouchRef = useRef(false);
  const hasFastSwipeRef = useRef(false);
  const fastSwipeModifierRef = useRef(0);
  const indexOnTouchStartRef = useRef(0);
  const initialScreenWidthRef = useRef(0);

  const getNewState = useCallback(
    (newIndex: number) => {
      const numberOfItems = React.Children.count(children);
      const slidesToShow = numberOfSlidesToShow;
      const lastIndex = Math.round(Math.max(0, numberOfItems - slidesToShow));
      const newIndexClamped = clamp(newIndex, 0, lastIndex);

      return {
        currentIndex: newIndexClamped,
        currentIndexRounded: Math.round(newIndexClamped),
        hasNextItem: newIndexClamped < lastIndex,
        hasPreviousItem: newIndexClamped > 0
      };
    },
    [children, numberOfSlidesToShow]
  );

  const [state, setState] = useState<CarouselState>(() => ({
    isDragging: false,
    isMounted: false,
    shouldShowNavigation: React.Children.count(children) > 1,
    ...getNewState(startIndex)
  }));

  const goToItem = useCallback(
    (newIndex: number) => {
      setState(prevState => ({
        ...prevState,
        ...getNewState(Math.round(newIndex)),
        isDragging: false
      }));
    },
    [getNewState]
  );

  const onTouchEnd = useCallback(() => {
    goToItem(state.currentIndex);
    itemsTouchStartXRef.current = undefined;
    travelRef.current = 0;
    previousXRef.current = 0;
  }, [goToItem, state.currentIndex]);

  const onTouchMove = useCallback(
    (e: TouchEvent, touch: Touch | undefined) => {
      isZoomedRef.current = Math.abs(window.innerWidth - initialScreenWidthRef.current) > 10;

      if (
        isAnimatingRef.current ||
        isZoomedRef.current ||
        hasMultiTouchRef.current ||
        !touch ||
        itemsTouchStartXRef.current === undefined ||
        !itemsWrapperRef.current
      ) {
        return;
      }

      const xDiff = itemsTouchStartXRef.current - touch.clientX;

      if (Math.abs(xDiff) > 5) {
        e.preventDefault();
      }

      if (Math.abs(touch.clientX - previousXRef.current) > 20) {
        hasFastSwipeRef.current = true;
        fastSwipeModifierRef.current = xDiff > 0 ? 1 : -1;
      }

      previousXRef.current = touch.clientX;
      travelRef.current = hasFastSwipeRef.current
        ? fastSwipeModifierRef.current
        : xDiff / itemsWrapperRef.current.offsetWidth;
      isAnimatingRef.current = true;

      setState(prevState => ({ ...prevState, isDragging: !hasFastSwipeRef.current }));

      // Use setTimeout to allow state update before calculating new state
      setTimeout(() => {
        setState(prevState => ({
          ...prevState,
          ...getNewState(indexOnTouchStartRef.current + travelRef.current)
        }));
        isAnimatingRef.current = false;
      }, 0);
    },
    [getNewState]
  );

  const onTouchStart = useCallback(
    (touch: Touch | undefined) => {
      if (!touch) {
        return;
      }

      itemsTouchStartXRef.current = touch.clientX;
      fastSwipeModifierRef.current = 0;
      hasFastSwipeRef.current = false;
      indexOnTouchStartRef.current = state.currentIndex;
      previousXRef.current = touch.clientX;
      travelRef.current = 0;
      setState(prevState => ({ ...prevState, isDragging: true }));
    },
    [state.currentIndex]
  );

  const onItemsWrapperTouchEnd = useCallback(() => {
    onTouchEnd();
  }, [onTouchEnd]);

  const onItemsWrapperTouchMove = useCallback(
    (e: TouchEvent) => {
      hasMultiTouchRef.current = e.touches.length > 1;
      onTouchMove(e, e.touches[0]);
    },
    [onTouchMove]
  );

  const onItemsWrapperTouchStart = useCallback(
    (e: TouchEvent) => {
      onTouchStart(e.touches[0]);
    },
    [onTouchStart]
  );

  const goToNextItem = useCallback(() => {
    goToItem(state.currentIndex + 1);
  }, [goToItem, state.currentIndex]);

  const goToPreviousItem = useCallback(() => {
    goToItem(state.currentIndex - 1);
  }, [goToItem, state.currentIndex]);

  const handleOnItemFocus = useCallback(
    (index: number) => {
      if (!itemsWrapperRef.current) return;

      // Scroll item into view if outside
      itemsWrapperRef.current.scroll({ left: 0 }); // Prevents automatic scroll-focus-target-to-view
      const isTargetOutsideView = index >= state.currentIndex + numberOfSlidesToShow || index < state.currentIndex;

      if (isTargetOutsideView) {
        setState(prevState => ({ ...prevState, ...getNewState(index) }));
        // Manually scroll page in Y axis
        const distanceToTopOfPage =
          itemsWrapperRef.current.getBoundingClientRect().top + window.pageYOffset - window.innerHeight / 2;
        window.scrollTo(0, distanceToTopOfPage);
      }
    },
    [state.currentIndex, numberOfSlidesToShow, getNewState]
  );

  // Handle children or numberOfSlidesToShow changes
  useEffect(() => {
    const childrenArray = React.Children.toArray(children);
    setState(prevState => ({
      ...prevState,
      shouldShowNavigation: childrenArray.length > 1,
      ...getNewState(startIndex)
    }));
  }, [children, numberOfSlidesToShow, startIndex, getNewState]);

  // Mount effect
  useEffect(() => {
    setState(prevState => ({ ...prevState, isMounted: true }));
    initialScreenWidthRef.current = window.innerWidth;

    const itemsWrapper = itemsWrapperRef.current;
    if (itemsWrapper) {
      itemsWrapper.addEventListener('touchend', onItemsWrapperTouchEnd);
      itemsWrapper.addEventListener('touchmove', onItemsWrapperTouchMove);
      itemsWrapper.addEventListener('touchstart', onItemsWrapperTouchStart);
    }

    return () => {
      if (itemsWrapper) {
        itemsWrapper.removeEventListener('touchend', onItemsWrapperTouchEnd);
        itemsWrapper.removeEventListener('touchmove', onItemsWrapperTouchMove);
        itemsWrapper.removeEventListener('touchstart', onItemsWrapperTouchStart);
      }
    };
  }, [onItemsWrapperTouchEnd, onItemsWrapperTouchMove, onItemsWrapperTouchStart]);

  const slidesToShow = numberOfSlidesToShow;
  const slidesCount = React.Children.count(children);
  const currentSlide = state.currentIndexRounded;
  const x = state.currentIndex;
  const itemWidth = 100 / slidesCount;

  if (slidesCount === 0) return null;

  return (
    <div
      className={cn('carousel', className, {
        'shows-multiple-slides': slidesToShow > 1
      })}
    >
      <div className="carousel-content">
        {state.shouldShowNavigation &&
          (previousButton ? (
            previousButton(goToPreviousItem, state.hasPreviousItem)
          ) : (
            <button
              className={cn('carousel-prev', themes(theme))}
              disabled={!state.hasPreviousItem}
              key="carousel-prev"
              onClick={goToPreviousItem}
              type="button"
            >
              <span>{previousLabel}</span>
            </button>
          ))}
        <div className="carousel-items-wrapper" ref={itemsWrapperRef}>
          <ul
            className="carousel-items"
            style={
              state.isMounted
                ? {
                    transform: `translateX(-${(itemWidth * x) / slidesToShow}%)`,
                    transition: state.isDragging ? 'none' : undefined,
                    width: `${slidesCount * 100}%`
                  }
                : undefined
            }
          >
            {React.Children.map(children, (child, index) => (
              <li
                key={index}
                className={cn({ 'is-current': currentSlide === index })}
                style={{ width: `${itemWidth / slidesToShow}%` }}
                onFocus={() => handleOnItemFocus(index)}
              >
                {child}
              </li>
            ))}
          </ul>
        </div>
        {state.shouldShowNavigation &&
          (nextButton ? (
            nextButton(goToNextItem, state.hasNextItem)
          ) : (
            <button
              className={cn('carousel-next', themes(theme))}
              disabled={!state.hasNextItem}
              key="carousel-next"
              onClick={goToNextItem}
              type="button"
            >
              <span>{nextLabel}</span>
            </button>
          ))}
      </div>
    </div>
  );
};

Carousel.displayName = 'Carousel';

export default Carousel;
